interface APIBaseUrlEnv {
  VITE_API_URL?: string
  NEXT_PUBLIC_ENV?: string
  DEV?: boolean
  PROD?: boolean
}

interface AixEnvResolutionInput {
  NEXT_PUBLIC_ENV?: string
  DEV?: boolean
  PROD?: boolean
  hostname?: string
}

/** Pure function for testing — accepts env values as parameters */
export const resolveAPIBaseUrl = (env: APIBaseUrlEnv): string => {
  // Same-origin proxy: both Vercel (rewrites) and local dev (Vite proxy) route /api/* to the API server.
  // NEXT_PUBLIC_ENV is set on Vercel deployments; DEV is set by Vite in local dev.
  // In both cases, return "" so requests go through the same-origin proxy.
  if (env.NEXT_PUBLIC_ENV || env.DEV) {
    return ""
  }
  // Override: explicit API URL (non-Vercel, non-local deployments)
  if (env.VITE_API_URL) {
    return env.VITE_API_URL
  }
  // Fallback for non-Vercel production
  if (env.PROD) {
    return "https://api.vidmax.ai"
  }
  return "http://localhost:8787"
}

export const getAPIBaseUrl = () =>
  resolveAPIBaseUrl({
    VITE_API_URL: import.meta.env.VITE_API_URL,
    NEXT_PUBLIC_ENV: import.meta.env.NEXT_PUBLIC_ENV,
    DEV: import.meta.env.DEV,
    PROD: import.meta.env.PROD,
  })

const E = import.meta.env

export const isProdVercelEnv = E.NEXT_PUBLIC_ENV === "prod" || E.PROD

/**
 * Environment enum for AIX applications (mirrors @aix/env EAixEnv)
 * Use EAixEnv.PROD instead of "prod" strings everywhere
 */
export enum EAixEnv {
  PROD = "prod",
  DEV = "dev",
  LOCAL = "local",
  TEST = "test",
}

/**
 * AIX_ENV - The canonical environment identifier for frontend
 */
export const resolveAixEnv = (env: AixEnvResolutionInput): EAixEnv => {
  const hostname = env.hostname?.toLowerCase()
  if (hostname === "localhost" || hostname === "127.0.0.1" || hostname === "[::1]") {
    return EAixEnv.LOCAL
  }
  if (env.NEXT_PUBLIC_ENV === "prod") return EAixEnv.PROD
  if (env.NEXT_PUBLIC_ENV === "dev" || env.NEXT_PUBLIC_ENV === "preview") return EAixEnv.DEV
  if (env.NEXT_PUBLIC_ENV === "local" || env.DEV) return EAixEnv.LOCAL
  if (env.PROD) return EAixEnv.PROD
  return EAixEnv.LOCAL
}

export const AIX_ENV: EAixEnv = resolveAixEnv({
  NEXT_PUBLIC_ENV: E.NEXT_PUBLIC_ENV,
  DEV: E.DEV,
  PROD: E.PROD,
  hostname: typeof window === "undefined" ? undefined : window.location.hostname,
})

export const IS_VERCEL_DEPLOYED = E.NEXT_PUBLIC_VERCEL === "1"

export const VIDMAX_UI_HOST = `${IS_VERCEL_DEPLOYED ? "https" : "http"}://${E.NEXT_PUBLIC_VIDMAX_UI_HOST}`

export const POSTHOG_KEY = E.NEXT_PUBLIC_POSTHOG_KEY || E.VITE_POSTHOG_KEY
